import React from 'react';
import {
	Chip,
	Tooltip,
	Typography,
	Box,
} from '@barracuda-internal/bds-core-v2';
import { DateTime } from 'luxon';
import { caseCloudTypeInfo } from '../unifiedreporting/helper';
import useStyles from 'src/styles/di-theme';
import classNames from 'classnames';

interface EllipsisTooltipProps {
	title: string;
	children: React.ReactNode;
}

export const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
	title,
	children,
}) => (
	<Tooltip
		title={
			<Box
				style={{
					maxHeight: '250px',
					overflowY: 'auto',
				}}
			>
				{typeof title === 'string'
					? title.split('\n').map((line, idx) => (
							<Typography key={idx} variant="body2">
								{line}
							</Typography>
					  ))
					: title}
			</Box>
		}
		arrow
		componentsProps={{
			tooltip: {
				style: {
					maxWidth: '400px',
					minWidth: '200px',
				},
			},
		}}
	>
		<Box
			style={{
				maxWidth: '250px',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}}
		>
			{children}
		</Box>
	</Tooltip>
);

export const renderCell = (
	accessorKey: string,
	cell: any,
	row?: any,
): React.ReactNode => {
	switch (accessorKey) {
		case 'scanDate': {
			const value = cell.getValue();
			if (!value) return 'N/A';

			const localZone = DateTime.local().zoneName;

			const parsedDate = DateTime.fromISO(value, { zone: 'utc' }).setZone(
				localZone,
			);
			return parsedDate.isValid
				? parsedDate.toFormat('MM/dd/yyyy, h:mm a')
				: 'Invalid Date';
		}

		case 'platforms': {
			const value = row?.original?.platform;
			return value?.length > 0 ? `${caseCloudTypeInfo(value)}` : '-';
		}

		case 'states': {
			return row?.original?.state ?? '-';
		}

		case 'file': {
			// To get the actual property from the data object:
			const fileName = row?.original?.fileName;
			return fileName?.length > 0 ? (
				<EllipsisTooltip title={fileName}>{fileName}</EllipsisTooltip>
			) : (
				'-'
			);
		}

		case 'filePath': {
			const filePath = cell.getValue();
			const value = `${filePath.join(' > ')}`;
			return filePath?.length > 0 ? (
				<EllipsisTooltip title={value}>{value}</EllipsisTooltip>
			) : (
				'-'
			);
		}

		case 'ownerNames': {
			const value = cell.getValue();
			if (value && value.length > 0) {
				return UserRendererCell(value);
			}
			return '-';
		}

		case 'decision': {
			const value = cell.getValue();
			return value && value?.length > 0 ? decisionRenderer(value) : '-';
		}

		case 'policies':
		case 'actions': {
			const value = cell.getValue();
			if (value && value.length > 0) {
				return UserRendererCell(value);
			}
			return '-';
		}

		default:
			return cell.getValue() ?? '-';
	}
};

export function UserRendererCell(ownerNames: string[]): JSX.Element {
	const tagsText = [];
	let count = 0;
	const moreTags = [];
	const classes = useStyles();
	ownerNames.forEach((value) => {
		if (count < 1) {
			tagsText.push(
				<Typography
					variant="body1"
					key={value}
					className={classNames(classes['pr_1'], classes['d_inline'])}
				>
					{value}
				</Typography>,
			);
		} else {
			moreTags.push(value);
		}
		count++;
	});

	return (
		<>
			<div>{tagsText.map((t) => t)} </div>
			<MoreRenderer tags={moreTags}></MoreRenderer>
		</>
	);
}

export function MoreRenderer({ tags }: { tags: string[] }): JSX.Element {
	const classes = useStyles();
	if (!tags || tags.length === 0) return null;

	return (
		<Tooltip
			title={
				<div>
					{tags.map((tag, idx) => (
						<Typography
							key={idx}
							variant="body2"
							style={{
								display: 'block',
								padding: '4px 8px',
								whiteSpace: 'pre-line',
							}}
						>
							{tag}
						</Typography>
					))}
				</div>
			}
			arrow
		>
			<Typography
				variant="body1"
				color="textSecondary"
				className={classNames(
					classes['d_inline'],
					classes['cursor_pointer'],
				)}
			>
				{' '}
				+{tags.length} more
			</Typography>
		</Tooltip>
	);
}

export function decisionRenderer(decision: string): JSX.Element {
	return (
		<Chip
			style={{
				margin: '2px',
				background: decision == 'Violation' ? '#FFDDDD' : '#DBF6CA',
				border: '0px',
				color: 'black',
			}}
			color="primary"
			clickable={false}
			label={<Typography component="span">{decision}</Typography>}
			size="small"
		/>
	);
}
